import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function TableNo21() {
  const pdfRef = useRef();

  // Data
  const ageGroupData = [
    { ageGroup: "14–18", year2018: 1514, year2024: 1941 },
    { ageGroup: "19–21", year2018: 4725, year2024: 5237 },
    { ageGroup: "22–24", year2018: 6439, year2024: 8006 },
    { ageGroup: "25–34", year2018: 25887, year2024: 31340 },
    { ageGroup: "35–44", year2018: 28447, year2024: 31699 },
    { ageGroup: "45–54", year2018: 24644, year2024: 29084 },
    { ageGroup: "55–64", year2018: 20827, year2024: 22159 },
    { ageGroup: "65–99", year2018: 5283, year2024: 8625 },
  ];

  // Color palette (one per age group)
  const colors = [
    "#FFA1B5", // 14–18
    "#86C7F3", // 19–21
    "#FFE29A", // 22–24
    "#93D9D9", // 25–34
    "#C2A3FF", // 35–44
    "#FFC58C", // 45–54
    "#B9D2AA", // 55–64
    "#6794D1", // 65–99
  ];

  // Compute summary
  const total2018 = ageGroupData.reduce((sum, d) => sum + d.year2018, 0);
  const total2024 = ageGroupData.reduce((sum, d) => sum + d.year2024, 0);
  const growthPct = (((total2024 - total2018) / total2018) * 100).toFixed(1);

  // Chart data
  const chartData = {
    labels: ageGroupData.map((d) => d.ageGroup),
    datasets: [
      {
        label: "2018",
        data: ageGroupData.map((d) => d.year2018),
        backgroundColor: "#F6CFFF",
        borderRadius: 6,
      },
      {
        label: "2024",
        data: ageGroupData.map((d) => d.year2024),
        backgroundColor: "#2C92B8",
        borderRadius: 6,
      },
    ],
  };

  // Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          color: "#111827",
          font: { size: 13, weight: "bold" },
        },
      },
      title: {
        display: true,
        text: "📊 Employment by Age Group (2018 vs 2024)",
        color: "#111827",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (ctx) =>
            `${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString("en-US")} employees`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { callback: (v) => v.toLocaleString("en-US"), color: "#4B5563" },
        title: {
          display: true,
          text: "Employment Count",
          color: "#111827",
          font: { size: 14, weight: "bold" },
        },
      },
      x: {
        ticks: { color: "#4B5563" },
        title: {
          display: true,
          text: "Age Groups",
          color: "#111827",
          font: { size: 14, weight: "bold" },
        },
      },
    },
  };

  // PDF export
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2, // higher for sharper text
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      // A4 size in mm
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Convert canvas height to mm
      const imgWidth = pageWidth - 10; // leave small margins
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      // Header
      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      // Footer
      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // More pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Footer for the last page
      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("employment_by_age_group.pdf");
      // input.style.width = originalWidth;
    });
  };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-teal-50 via-white to-red-50 rounded-xl shadow-lg"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Employment by Age Group (2018 vs 2024)
        </h2>

        {/* Insight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-teal-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Total Employment (2018)</p>
            <p className="text-xl font-bold text-teal-800">
              {total2018.toLocaleString("en-US")}
            </p>
          </div>
          <div className="p-4 bg-red-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Total Employment (2024)</p>
            <p className="text-xl font-bold text-red-800">
              {total2024.toLocaleString("en-US")}
            </p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Overall Growth</p>
            <p className="text-xl font-bold text-green-700">+{growthPct}%</p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="overflow-x-auto rounded-lg shadow-md">
            <table className="w-full border-collapse text-center">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Age Group</th>
                  <th className="p-2">2018</th>
                  <th className="p-2">2024</th>
                  <th className="p-2">Growth %</th>
                </tr>
              </thead>
              <tbody>
                {ageGroupData.map((row, i) => {
                  const growth = (
                    ((row.year2024 - row.year2018) / row.year2018) *
                    100
                  ).toFixed(1);
                  return (
                    <tr
                      key={i}
                      className="even:bg-gray-50 odd:bg-white hover:bg-gray-100"
                    >
                      <td className="p-2 font-semibold">{row.ageGroup}</td>
                      <td className="p-2 text-teal-700">
                        {row.year2018.toLocaleString("en-US")}
                      </td>
                      <td className="p-2 text-red-700">
                        {row.year2024.toLocaleString("en-US")}
                      </td>
                      <td
                        className={`p-2 font-medium ${
                          growth > 0 ? "text-green-700" : "text-gray-600"
                        }`}
                      >
                        {growth}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="text-center mt-4 text-gray-700">
          <p className="text-sm">
            📌 Utah’s employment grew by{" "}
            <span className="font-semibold text-green-700">{growthPct}%</span>{" "}
            from 2018 to 2024, led by the{" "}
            <span className="font-semibold text-red-700">
              25–44 age brackets
            </span>
            .
          </p>
        </div>
      </div>

      {/* PDF Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
